import * as React from "react";
import "../styles/global.css";
import { Link } from "gatsby";
import { SEO } from "../components/seo";
import { ReactNode } from "react";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import mixpanel from "mixpanel-browser";
import { StaticImage } from "gatsby-plugin-image";

const IndexPage = () => (
  <>
    <Navbar />
    <div className="max-w-3xl mx-auto p-6">
      <HeroText />
      <Work />
    </div>
  </>
);

const Navbar = () => (
  <div className="flex flex-row justify-end pr-4 md:pr-6 sticky top-0 bg-white">
    <span className="flex-1 p-4">
      <StaticImage
        src="../assets/logo4.png"
        alt="LYT Personal Logo"
        height={32}
      />
    </span>
    <OutboundLink
      className="py-4 px-2 md:py-6 md:px-8"
      href="https://linkedin.com/in/yenter/"
      onClick={() => mixpanel.track("View LinkedIn", { Location: "Navbar" })}
      target="_blank"
      rel="noopener noreferrer"
    >
      LinkedIn
    </OutboundLink>
    <Link
      className="py-4 px-2 md:py-6 md:px-8"
      to="/cv"
      onClick={() => mixpanel.track("View CV")}
    >
      CV
    </Link>
  </div>
);

const HeroText = () => (
  <div>
    <h1 className="text-4xl md:text-5xl font-bold mt-10 leading-snug font-serif">
      Hey there, <span className="text-nowrap">I'm Yen Ter!</span>
    </h1>
    <div className="md:text-lg text-zinc-500 mt-3 md:mt-5 ml-5 md:ml-12 italic font-serif">
      Full-Stack Software Engineer | AI/ML &{" "}
      <span className="text-nowrap">Data Science</span> Expert |{" "}
      <span className="md:text-nowrap">Agile & XP Practitioner</span> |{" "}
      <span className="text-nowrap">Public Speaker</span> & Mentor | Founder &
      CEO
    </div>
  </div>
);

const Work = () => (
  <div className="flex flex-col gap-4 mt-16">
    <p>
      With 5 years of experience in full-stack software engineering, I have
      delivered high-impact solutions across a variety of industries, including
      healthcare, transportation, education, and telecom. My expertise includes{" "}
      <strong>web applications</strong>, <strong>AI/ML</strong> and{" "}
      <strong>data products</strong>.
    </p>

    <p>
      Over my career, I have had the privilege of{" "}
      <strong>empowering and leading teams</strong> using{" "}
      <strong>Agile and Extreme Programming practices,</strong> with a strong
      emphasis on <strong>creating value for both businesses and users.</strong>{" "}
      Having closely collaborated in <strong>cross-functional teams</strong>{" "}
      across Singapore 🇸🇬, the UK 🇬🇧, Australia 🇦🇺, and Japan 🇯🇵, I strongly
      believe that <strong>clear communication</strong> and{" "}
      <strong>strong integration within and across disciplines</strong> is
      crucial in building resilient teams that deliver exceptional results.
    </p>
    <WorkExamples />
    <p>
      I also strongly believe in <strong>continuous learning and growth</strong>
      , for myself and the team. This can be seen through the{" "}
      <OutboundLink
        href="https://kaphos.sg"
        onClick={() => mixpanel.track("View Kaphos")}
        target="_blank"
      >
        tech consultancy I founded
      </OutboundLink>
      , which I intentionally structure and run with a focus on providing the
      team with opportunities to learn through practice. While I also actively
      pursue informal learning from colleagues and clients at work, I also value
      formal learning, and undertook two Masters—one in Computer Science
      (Georgia Tech), and another in Christian Studies (Regent College)—to
      upskill myself and bridge knowledge and practice gaps.
    </p>

    <p>
      I believe in value-centred and people-centred work, and I get excited
      about opportunities to <strong>create huge positive impact</strong> with{" "}
      <strong>
        great learning and growth opportunities for myself and others.
      </strong>{" "}
      If this sounds like the kind of work you do, I would love to connect with
      you on either{" "}
      <OutboundLink
        href="https://linkedin.com/in/yenter/"
        onClick={() => mixpanel.track("View LinkedIn", { Location: "Body" })}
        target="_blank"
        rel="noopener noreferrer"
      >
        LinkedIn
      </OutboundLink>{" "}
      or via email at{" "}
      <OutboundLink
        href="mailto:work@yenter.com"
        onClick={() => mixpanel.track("Email")}
      >
        work@yenter.com
      </OutboundLink>
      .
    </p>
  </div>
);

const WorkExamples = () => (
  <>
    <p>Some examples include:</p>
    <div className="flex flex-col gap-3 md:gap-2 mb-6">
      <WorkExample dataNumber="> 1,500" dataCaption="DAUs">
        <strong>Modernised a complex legacy system</strong> for an Australian
        healthcare services provider,{" "}
        <strong>with more than 1,500 daily active users</strong> and{" "}
        <strong>saving over 285 working days since launch</strong>.
      </WorkExample>
      <WorkExample dataNumber="< 12" dataCaption="weeks">
        <strong>Developed a ticketless booking system</strong> for a major
        Japanese railway company that interoperates with legacy systems, to
        validate the applicability of Agile methodologies for mission-critical
        systems, with{" "}
        <strong>
          the MVP released in less than 12 weeks since project inception
        </strong>
        .
      </WorkExample>
      <WorkExample dataNumber="> 100TB" dataCaption="per day">
        <strong>
          Developed and modernised big data pipelines, dashboards, and models
        </strong>{" "}
        for Singapore’s largest telco, with some data flows processing{" "}
        <strong>over 100TB per day,</strong> to improve consumer experience and
        develop business opportunities.
      </WorkExample>
    </div>
  </>
);

const WorkExample = ({
  children,
  dataCaption,
  dataNumber,
}: {
  dataNumber: string;
  dataCaption: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col md:flex-row gap-4 bg-zinc-50 p-5 rounded">
    <div className="flex flex-col md:w-32 justify-center items-center pt-3 md:pt-0">
      <div className="font-serif text-3xl font-semibold">{dataNumber}</div>
      <div className="font-serif text-xl text-zinc-500 mt-0.5">
        {dataCaption}
      </div>
    </div>
    <p className="flex-1 text-zinc-700">{children}</p>
  </div>
);

export default IndexPage;

export const Head = () => <SEO pathname="/" />;
